import { ViewportRuler } from '@angular/cdk/scrolling';
import { CommonModule, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject
} from '@angular/core';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { ExtendedMap, LegendParams, Plugin, PluginMap } from '@jeppesen-foreflight/jmkw-types';
import { Observable, Subject, map, merge, share, startWith } from 'rxjs';
import { store } from '../../jmkw.store';
import { LegendWrapperComponent } from './legend-wrapper/legend-wrapper.component';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

@Component({
  selector: 'jmkw-legends',
  standalone: true,
  imports: [CommonModule, LegendWrapperComponent],
  templateUrl: './legends.component.html',
  styleUrls: ['./legends.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LegendsComponent implements OnInit, OnDestroy {
  private readonly _viewportRuler = inject(ViewportRuler);
  private readonly _cdr = inject(ChangeDetectorRef);

  private readonly _scrollBy = 250;
  private _visibilityHandle?: ResourceHandle;

  readonly trigger = new Subject<void>();

  showButtons$: Observable<boolean> = merge(
    this.trigger,
    this._viewportRuler.change(200)
  ).pipe(
    map(() => this._shouldShowButtons()),
    startWith(this._shouldShowButtons()),
    share()
  );

  private _legendsContainer!: ElementRef<HTMLElement>;

  @ViewChild('legendsContainer', { read: ElementRef })
  set legendsContainer(container: ElementRef<HTMLElement>) {
    if (container) {
      this._legendsContainer = container;
    }
  }

  @ViewChild('leftButton', { static: false, read: ElementRef })
  private _leftButton!: ElementRef<HTMLButtonElement>;

  @ViewChild('rightButton', { static: false, read: ElementRef })
  private _rightButton!: ElementRef<HTMLButtonElement>;

  legendMap = computed(
    () => store.pluginMap()?.allLegends() ?? new ExtendedMap<LegendParams>()
  );

  visibleLayers = new Set<string>();

  ngOnInit(): void {
    this._setupVisibilityTracking();
  }

  ngOnDestroy(): void {
    this._visibilityHandle?.remove();
  }

  private _shouldShowButtons(): boolean {
    if (!this._legendsContainer?.nativeElement) {
      return false;
    }

    const { clientWidth = 0, scrollWidth = 0 } =
      this._legendsContainer.nativeElement;

    const leftButtonWidth = this._leftButton?.nativeElement?.clientWidth ?? 0;
    const rightButtonWidth = this._rightButton?.nativeElement?.clientWidth ?? 0;

    return scrollWidth > clientWidth + (leftButtonWidth + rightButtonWidth);
  }

  private _setupVisibilityTracking(): void {
    this._visibilityHandle = this._trackMapVisibilityChanges();
  }

  /**
   * Tracks the visibility changes of the map layers and updates the visible layers.
   * Uses the map as the source of truth for layer visibility.
   */
  private _trackMapVisibilityChanges(): ResourceHandle {
    return reactiveUtils.watch(
      () => {
        if (!store.map()?.layers) {
          return [];
        }

        return store
          .map()
          .layers.reduce<
            string[]
          >((acc, layer) => (layer?.visible ? [...acc, layer.id] : acc), []);
      },
      (visibleLayerIDs) => {
        const pluginMap = store.pluginMap();
        if (!pluginMap) {
          console.warn('Plugin map is not available');
          return;
        }

        this.visibleLayers = this._filterPluginMapByIdsWithLegend(
          pluginMap,
          new Set(visibleLayerIDs)
        );

        // Schedule UI updates
        setTimeout(() => {
          this._cdr.detectChanges();
          this.trigger.next();
        });
      }
    );
  }

  private _filterPluginMapByIdsWithLegend(
    pluginMap: PluginMap,
    visibleLayerIDs: Set<string>
  ): Set<string> {
    if (!pluginMap || !visibleLayerIDs.size) {
      return new Set<string>();
    }

    const resultSet = new Set<string>();

    const traverse = (id: string, plugin: Plugin): boolean => {
      if (!id || !plugin) {
        return false;
      }

      let pluginIdFound = visibleLayerIDs.has(id);

      // Check children recursively
      if (plugin.children?.size) {
        for (const [childId, childPlugin] of plugin.children) {
          if (childId && childPlugin && traverse(childId, childPlugin)) {
            pluginIdFound = true;
          }
        }
      }

      // Add to result set if conditions are met
      if (pluginIdFound && plugin.legend && !plugin.legend.disabled) {
        resultSet.add(id);
      }

      return pluginIdFound;
    };

    // Process all plugins
    for (const [id, plugin] of pluginMap) {
      if (id && plugin) {
        traverse(id, plugin);
      }
    }

    return resultSet;
  }

  public sort = (
    a: KeyValue<string, LegendParams>,
    b: KeyValue<string, LegendParams>
  ): number => {
    if (!a?.value?.order || !b?.value?.order) {
      return 0;
    }

    if (a.value.order > b.value.order) return -1;
    if (b.value.order > a.value.order) return 1;
    return 0;
  };

  public scroll(direction: 'right' | 'left'): void {
    if (!this._legendsContainer?.nativeElement) {
      return;
    }

    const adjustmentAmount =
      direction === 'right' ? this._scrollBy : -this._scrollBy;
    this._legendsContainer.nativeElement.scrollLeft += adjustmentAmount;
  }
}
